import { CompositionStage, useComposition, animate, Easing, clamp, lerp } from "@/components/hero/stage";

/* Sleep hero — "the night, built backwards".
   Dusk: a building puts its windows out, one by one.
   Runway: the three hours that decide tonight slide onto a gold spine.
   Cycles: a real hypnogram draws itself, four cycles deep to light.
   Dawn: the light returns and the hour that never moves is stated. */

const W = 820, H = 760;
const GOLD = "#EBD9A6";
const FONT = "Outfit, Poppins, system-ui, sans-serif";
const SCENES = [
  { name: "Dusk", dur: 5 },
  { name: "Runway", dur: 5 },
  { name: "Cycles", dur: 5.5 },
  { name: "Dawn", dur: 4.5 },
];

const rnd = (i, s) => { const x = Math.sin(i * 27.13 + s * 91.7) * 43758.5453; return x - Math.floor(x); };
const STARS = Array.from({ length: 26 }, (_, i) => ({ x: rnd(i, 1) * W, y: rnd(i, 2) * 340, r: 0.9 + rnd(i, 3) * 1.8, ph: rnd(i, 4) }));
const WINDOWS = Array.from({ length: 20 }, (_, i) => ({ c: i % 5, r: Math.floor(i / 5), late: i === 7, three: i === 13 }));

const fade = (T, a, b, d = 0.55) => Math.min(clamp((T - a) / d, 0, 1), clamp((b - T) / d, 0, 1));

const HOURS = [
  { t: "13:30", l: "last coffee", y: 150 },
  { t: "22:00", l: "lamps only, nothing overhead", y: 292 },
  { t: "23:30", l: "lights out", y: 434, key: true },
];

/* four cycles: awake 0, rem 1, light 2, deep 3 */
const HYPNO = [0, 2, 3, 3, 2, 1, 2, 3, 2, 1, 2, 2, 1, 2, 1, 1, 0];

function Night({ accent = GOLD }) {
  const { T: rawT, CUES: rawCues, authoredTotal } = useComposition();
  const CUES = { Dusk: 0, Runway: 5, Cycles: 10, Dawn: 15.5, ...rawCues };
  const T = Number.isFinite(rawT) ? rawT : 0;
  const total = authoredTotal || 20;
  const A = (o) => animate(o)(T);

  const dusk = fade(T, CUES.Dusk - 0.2, CUES.Runway - 0.15, 0.4);
  const runway = fade(T, CUES.Runway + 0.05, CUES.Cycles - 0.15, 0.4);
  const cycles = fade(T, CUES.Cycles + 0.05, CUES.Dawn - 0.15, 0.4);
  const dawn = fade(T, CUES.Dawn + 0.05, total - 0.05, 0.4);

  /* the moon crosses, the sun answers */
  const moonP = A({ from: 0, to: 1, start: 0, end: CUES.Dawn + 1, ease: Easing.linear });
  const moonX = lerp(96, 748, moonP);
  const moonY = 132 - Math.sin(moonP * Math.PI) * 96;
  const sun = A({ from: 0, to: 1, start: CUES.Dawn, end: total - 0.4, ease: Easing.easeOutCubic });

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: FONT }}>
      {/* stars, dimming as dawn arrives */}
      {STARS.map((s, i) => (
        <span key={"s" + i} style={{
          position: "absolute", left: s.x, top: s.y, width: s.r * 2, height: s.r * 2, borderRadius: "50%",
          background: i % 6 === 0 ? accent : "#fff",
          opacity: (0.2 + 0.65 * Math.abs(Math.sin(T * 0.7 + s.ph * 6))) * (1 - sun * 0.95),
        }} />
      ))}

      {/* the moon */}
      <div style={{
        position: "absolute", left: moonX - 34, top: moonY - 34, width: 68, height: 68, borderRadius: "50%",
        background: "radial-gradient(circle at 36% 30%, #FFF7E2, #E4D2A4 58%, #B7A87F)",
        boxShadow: "0 0 70px 18px rgba(235,217,166," + (0.2 * (1 - sun)) + ")", opacity: 1 - sun * 0.9,
      }} />

      {/* dawn light on the horizon */}
      <div style={{
        position: "absolute", left: -80, right: -80, bottom: -140 + sun * 40, height: 420,
        borderRadius: "50%",
        background: "radial-gradient(50% 100% at 32% 100%, rgba(255,214,150,0.55), rgba(235,217,166,0.18) 46%, transparent 72%)",
        opacity: sun,
      }} />

      {/* ---------------------------------------------- the building */}
      <div style={{ position: "absolute", left: 18, bottom: -40, width: 240, opacity: 0.6 + dusk * 0.4 }}>
        <div style={{ position: "relative", height: 420, borderRadius: "12px 12px 0 0", background: "linear-gradient(180deg, rgba(24,31,76,0.95), rgba(12,16,44,0.98))", border: "1px solid rgba(235,217,166,0.2)", boxShadow: "0 30px 70px -40px rgba(0,0,0,0.9)" }}>
          {WINDOWS.map((w, i) => {
            const off = CUES.Dusk + 0.5 + i * 0.19;
            let lit = 1 - A({ from: 0, to: 1, start: off, end: off + 0.5, ease: Easing.easeOutQuad });
            if (w.late) lit = Math.max(lit, 1 - A({ from: 0, to: 1, start: CUES.Runway + 1.6, end: CUES.Runway + 2.3, ease: Easing.easeOutQuad }));
            if (w.three) lit = Math.max(lit, fade(T, CUES.Cycles + 2.2, CUES.Dawn - 0.6, 0.5) * 0.9);
            return (
              <span key={"w" + i} style={{
                position: "absolute", left: 22 + w.c * 40, top: 30 + w.r * 70, width: 28, height: 38, borderRadius: 4,
                background: lit > 0.02 ? "rgba(255,226,168," + (0.28 + lit * 0.72) + ")" : "rgba(255,255,255,0.045)",
                boxShadow: lit > 0.3 ? "0 0 18px rgba(255,222,160," + lit * 0.7 + ")" : "none",
              }} />
            );
          })}
        </div>
      </div>

      {/* ---------------------------------------------- scene captions */}
      <div style={{ position: "absolute", left: 292, top: 232, opacity: dusk }}>
        <div style={{ font: "500 13px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(255,255,255,0.45)" }}>21:00 · THE STREET GOES DARK</div>
        <div style={{ font: "400 30px/1.2 " + FONT, color: "#fff", marginTop: 14, maxWidth: 400 }}>
          Nobody decides to sleep.<br />The light decides it for them.
        </div>
      </div>

      {/* ---------------------------------------------- the runway spine */}
      <div style={{ position: "absolute", left: 300, top: 100, opacity: runway }}>
          <div style={{ position: "absolute", left: 0, top: 0, width: 2, height: 400, background: "linear-gradient(180deg, rgba(235,217,166,0.08), " + accent + " 34%, rgba(107,116,220,0.85))" }} />
        {HOURS.map((hr, i) => {
          const at = CUES.Runway + 0.35 + i * 0.85;
          const p = A({ from: 0, to: 1, start: at, end: at + 0.85, ease: Easing.easeOutCubic });
          return (
            <div key={hr.t} style={{
              position: "absolute", left: 26, top: hr.y - 100, display: "flex", alignItems: "center", gap: 16,
              opacity: p, transform: "translateX(" + (1 - p) * 90 + "px)",
            }}>
              <span style={{
                width: hr.key ? 15 : 11, height: hr.key ? 15 : 11, borderRadius: "50%", marginLeft: -33,
                background: hr.key ? accent : "#141A40", border: hr.key ? "none" : "2px solid " + accent,
                boxShadow: hr.key ? "0 0 0 7px rgba(235,217,166,0.16)" : "none",
              }} />
              <span style={{ font: "500 34px/1 " + FONT, color: hr.key ? accent : "rgba(255,255,255,0.9)", fontVariantNumeric: "tabular-nums" }}>{hr.t}</span>
              <span style={{ font: "400 15.5px/1.35 " + FONT, color: "rgba(255,255,255,0.52)", width: 210 }}>{hr.l}</span>
            </div>
          );
        })}
        <div style={{ position: "absolute", left: 26, top: 402, whiteSpace: "nowrap", font: "500 13px/1 " + FONT, letterSpacing: "0.18em", color: "rgba(255,255,255,0.35)" }}>
          BUILT BACKWARDS FROM 07:00
        </div>
      </div>

      {/* ---------------------------------------------- the hypnogram */}
      <div style={{ position: "absolute", left: 268, top: 196, width: 520, opacity: cycles }}>
        <div style={{ font: "500 13px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(255,255,255,0.45)" }}>FOUR CYCLES · DEEP FIRST, DREAMS LAST</div>
        <svg width="520" height="230" viewBox="0 0 520 230" style={{ marginTop: 22, overflow: "visible" }}>
          {["AWAKE", "REM", "LIGHT", "DEEP"].map((l, i) => (
            <g key={l}>
              <line x1="0" y1={22 + i * 58} x2="520" y2={22 + i * 58} stroke="rgba(255,255,255,0.09)" strokeDasharray="3 6" />
              <text x="-8" y={26 + i * 58} textAnchor="end" fontSize="11" fill="rgba(255,255,255,0.35)" fontFamily={FONT} letterSpacing="1.4">{l}</text>
            </g>
          ))}
          {(() => {
            const p = A({ from: 0, to: 1, start: CUES.Cycles + 0.5, end: CUES.Cycles + 4.2, ease: Easing.easeInOutCubic });
            const n = HYPNO.length - 1;
            const upto = p * n;
            const seg = [];
            for (let i = 0; i <= Math.floor(upto); i++) seg.push([(i / n) * 520, 22 + HYPNO[i] * 58]);
            const li = Math.floor(upto), hi = Math.min(n, li + 1), f = upto - li;
            seg.push([lerp((li / n) * 520, (hi / n) * 520, f), lerp(22 + HYPNO[li] * 58, 22 + HYPNO[hi] * 58, f)]);
            const d = seg.map((s, i) => (i ? "L " : "M ") + s[0].toFixed(1) + " " + s[1].toFixed(1)).join(" ");
            const last = seg[seg.length - 1];
            return (
              <g>
                <path d={d + " L " + last[0].toFixed(1) + " 230 L 0 230 Z"} fill="rgba(107,116,220,0.16)" />
                <path d={d} fill="none" stroke={accent} strokeWidth="3.4" strokeLinejoin="round" strokeLinecap="round" />
                <circle cx={last[0]} cy={last[1]} r="12" fill={accent} opacity="0.22" />
                <circle cx={last[0]} cy={last[1]} r="5.5" fill={accent} />
              </g>
            );
          })()}
        </svg>
        <div style={{ display: "flex", gap: 26, marginTop: 26 }}>
          {[["7h 30m", "in bed"], ["1h 48m", "deep, mostly before 02:00"], ["4", "cycles, uninterrupted"]].map(([a, b], i) => {
            const at = CUES.Cycles + 2.6 + i * 0.3;
            const p = A({ from: 0, to: 1, start: at, end: at + 0.7, ease: Easing.easeOutCubic });
            return (
              <div key={b} style={{ opacity: p, transform: "translateY(" + (1 - p) * 16 + "px)" }}>
                <div style={{ font: "500 26px/1 " + FONT, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{a}</div>
                <div style={{ font: "400 12.5px/1.4 " + FONT, color: "rgba(255,255,255,0.45)", marginTop: 6, maxWidth: 150 }}>{b}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------------------------------------------- dawn */}
      <div style={{ position: "absolute", left: 288, top: 208, opacity: dawn }}>
        <div style={{ font: "500 13px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(255,255,255,0.5)" }}>AND THE ONE HOUR THAT NEVER MOVES</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 18, marginTop: 16 }}>
          <div style={{ font: "500 118px/0.9 " + FONT, color: accent, letterSpacing: "-0.04em", fontVariantNumeric: "tabular-nums", textShadow: "0 20px 60px rgba(235,217,166,0.3)" }}>07:00</div>
          <div style={{ font: "400 18px/1.4 " + FONT, color: "rgba(255,255,255,0.55)", paddingBottom: 16, maxWidth: 170 }}>every day, including the ones you resent</div>
        </div>
        <div style={{
          marginTop: 34, padding: "22px 26px", borderRadius: 22, maxWidth: 430,
          background: "linear-gradient(140deg, rgba(255,255,255,0.1), rgba(235,217,166,0.07))",
          border: "1px solid rgba(235,217,166,0.28)", backdropFilter: "blur(8px)",
          opacity: A({ from: 0, to: 1, start: CUES.Dawn + 1.1, end: CUES.Dawn + 2.4, ease: Easing.easeOutCubic }),
        }}>
          <div style={{ font: "500 12px/1 " + FONT, letterSpacing: "0.18em", color: accent }}>TONIGHT'S RUNWAY OPENS AT</div>
          <div style={{ font: "500 40px/1 " + FONT, color: "#fff", marginTop: 10, fontVariantNumeric: "tabular-nums" }}>22:00</div>
          <div style={{ font: "400 14.5px/1.5 " + FONT, color: "rgba(255,255,255,0.55)", marginTop: 10 }}>
            Ninety minutes of descent, in the order your body reads.
          </div>
        </div>
      </div>

      {/* scene dots */}
      <div style={{ position: "absolute", right: 6, bottom: 8, display: "flex", gap: 7 }}>
        {SCENES.map((s, i) => {
          const on = (i === 0 && T < CUES.Runway) || (i === 1 && T >= CUES.Runway && T < CUES.Cycles) || (i === 2 && T >= CUES.Cycles && T < CUES.Dawn) || (i === 3 && T >= CUES.Dawn);
          return <span key={s.name} style={{ width: on ? 22 : 8, height: 8, borderRadius: 4, background: on ? accent : "rgba(255,255,255,0.2)", transition: "width 0.4s" }} />;
        })}
      </div>
    </div>
  );
}

export default function SleepHero({ accent = GOLD }) {
  return (
    <CompositionStage width={W} height={H} scenes={SCENES} minScale={0.58} maxHeight={640}>
      <Night accent={accent} />
    </CompositionStage>
  );
}
